import { useMemo, useState } from 'react';

import { DragDropContext, type DropResult } from '@hello-pangea/dnd';
import styled from 'styled-components';

import type { PlanningTableRow } from '../constants/dynamicDeliveryColumns';
import { PlanningMap } from './PlanningMap';
import { PlanningTourList } from './PlanningTourList';

type PlanningMapViewProps = {
    rows: PlanningTableRow[];
    selectedKeys: string[];
    onSelect: (ids: string[]) => void;
    focusedApproIds: string[];
    onFocusAppro: (approId: string) => void;
    extraAppros: { _id: string; name: string }[];
    onAddAppro: () => void;
    collapseAll: boolean;
    collapseToken: number;
    insertMode: boolean;
    onPickRegroupement: (approId: string, rank: number) => void;
    onDragEnd: (result: DropResult) => void;
    // Tour edit mode: clicking a regroupement card / stop marker (de)selects the whole regroupement.
    editMode: boolean;
    // Selected regroupement key (`${approId}|${rank}`) -> 1-based selection (click) order. Membership AND number.
    selectionNumberByKey: Map<string, number>;
    onToggleRegroupement: (approId: string, ranks: number[]) => void;
    // Clic sur le titre de la légende : (dé)focus toutes les tournées d'un coup.
    onToggleAllFocus: () => void;
    onAddPotential: (columnPlanningIds: string[], toApproId: string) => void;
    // Map display mode: real road itineraries (IGN) vs « vol d'oiseau » straight lines. Forwarded to the map.
    roadRouting: boolean;
};

// Carte view: tour list (left) + map (right), wired to the same selection/focus/insert handlers. The
// drag-active flag is local (only the tour list reacts to it), so it lives here rather than in the page.
export const PlanningMapView = ({
    rows,
    selectedKeys,
    onSelect,
    focusedApproIds,
    onFocusAppro,
    extraAppros,
    onAddAppro,
    collapseAll,
    collapseToken,
    insertMode,
    onPickRegroupement,
    onDragEnd,
    editMode,
    selectionNumberByKey,
    onToggleRegroupement,
    onToggleAllFocus,
    onAddPotential,
    roadRouting,
}: PlanningMapViewProps) => {
    const [isTourDragActive, setIsTourDragActive] = useState(false);
    const focusedSet = useMemo(() => new Set(focusedApproIds), [focusedApproIds]);

    // Props shared by the left list, whether in stack (normal view) or columns (edit-mode board) layout.
    const listProps = {
        rows,
        selectedKeys,
        onSelect,
        focusedApproIds,
        onFocusAppro,
        isDragActive: isTourDragActive,
        collapseAll,
        collapseToken,
        insertMode,
        onPickRegroupement,
        editMode,
        selectionNumberByKey,
        onToggleRegroupement,
        onAddPotential,
    };

    // ONE DragDropContext (drag within a column = reorder, across columns = reassign). In edit mode the left
    // list becomes the COLUMNS of the focused tours and the map stays full-height on the right; the tours to
    // compare are picked via the map legend / routes (which drive the focus).
    // The map is always mounted → the planner sees the routes reshape as stops are dragged.
    return (
        <DragDropContext
            onDragStart={() => setIsTourDragActive(true)}
            onDragEnd={(result) => {
                setIsTourDragActive(false);
                onDragEnd(result);
            }}
        >
            <MapSplit>
                <PlanningTourList
                    {...listProps}
                    extraAppros={editMode ? [] : extraAppros}
                    onAddAppro={onAddAppro}
                    approFilter={editMode ? (approId) => focusedSet.has(approId) : undefined}
                    layout={editMode ? 'columns' : 'stack'}
                    showAddAppro={!editMode}
                />
                <PlanningMap
                    rows={rows}
                    selectedKeys={selectedKeys}
                    onSelect={onSelect}
                    focusedApproIds={focusedApproIds}
                    onFocusAppro={onFocusAppro}
                    insertMode={insertMode}
                    onPickRegroupement={onPickRegroupement}
                    editMode={editMode}
                    selectionNumberByKey={selectionNumberByKey}
                    onToggleRegroupement={onToggleRegroupement}
                    onToggleAllFocus={onToggleAllFocus}
                    roadRouting={roadRouting}
                />
            </MapSplit>
        </DragDropContext>
    );
};

// Carte view: tour list (left, fixed width) + map (right, fills) side by side, like the validated prototype.
const MapSplit = styled.div`
    flex: 1;
    display: flex;
    flex-direction: row;
    gap: 12px;
    min-height: 0;
    /* Bound the width to the available area (otherwise the board columns — wide content — push the map out of
       the clipped container and the columns' max-width 60% resolves against nothing). */
    min-width: 0;
`;
